import { useCallback, useMemo, useRef } from "react";
import { FolderOpen } from "lucide-react";
import { Dropdown, Button } from "semantic-ui-react";

import ACL_RELATIONSHIPS from "acl-relationships";
import usePageTitle from "hooks/usePageTitle";
import BaseTableV3 from "components/BaseTableV3/BaseTableV3";
import withRoleCheck from "components/hocs/withRoleCheck";
import CaseService from "services/Case";
import { useCaseTable } from "hooks/useCaseTable";
import { getNextQueryParams } from "components/helpers";
import useAuthorized from "hooks/useAuthorized";
import _AssignInvestigatorsModal from "./AssignInvestigatorsModal";
import { UpdateCampaignCaseModal } from "./UpdateCampaignCaseModal";
import ConfirmationModal from "components/modals/ConfirmationModal";
import { omitByNil } from "helpers/tables";

export const CASES_TABLE_CONSTANTS = {
  tableName: "cases",
  className: "Case",
  exportTableName: "CasesTable",
  header: "Cases",
  headerIcon: FolderOpen,
};

const AssignInvestigatorsModal = withRoleCheck(_AssignInvestigatorsModal, [
  ACL_RELATIONSHIPS.case.edit,
]);

const checkCaseEntityValidity = (cases, checkedIds) => {
  const validCases = [];
  const invalidCases = [];

  cases.forEach(row => {
    const isEntityValid = row.entity_id && row.entity_id !== 0;
    const isSelected = checkedIds.includes(row.id);
    if (isSelected) {
      isEntityValid
        ? validCases.push(row.id)
        : invalidCases.push({
            id: row.id,
            name: row.name,
            case_id: row.case_id,
          });
    }
  });

  return { caseIds: validCases, invalidCases: invalidCases };
};

const CasesTableV3 = ({ campaignId = null, entityId = null }) => {
  const enableTags = useAuthorized(
    [
      ACL_RELATIONSHIPS.caseTags.create,
      ACL_RELATIONSHIPS.caseTags.read,
      ACL_RELATIONSHIPS.caseTags.delete,
    ],
    { partialMatch: true }
  );
  const isStandalone = !campaignId && !entityId;
  usePageTitle(isStandalone ? CASES_TABLE_CONSTANTS.header : null);

  const defaultQueryParams = useMemo(
    () =>
      omitByNil({
        campaign_id: campaignId,
        entity_id: entityId,
        in_campaign: !!campaignId,
      }),
    [campaignId, entityId]
  );

  const { filters, columns, customFields, queryKey, invalidateData } =
    useCaseTable({
      campaignId,
      entityId,
      className: CASES_TABLE_CONSTANTS.className,
    });

  const addCampaignCaseModalRef = useRef();
  const addSingleCampaignCaseModalRef = useRef();
  const updateAssigneeModalRef = useRef();

  const fetchCases = useCallback(
    async queryParams => {
      try {
        const nextQueryParams = getNextQueryParams(queryParams, columns);

        const response = await CaseService.getTable({
          ...nextQueryParams,
          ...defaultQueryParams,
        });
        return response;
      } catch (error) {
        console.error("Error fetching cases:", error);
        throw error;
      }
    },
    [columns, defaultQueryParams]
  );

  const checkSelectedRowsInCampaign = (rows, checkedIds) => {
    let inCampaign = true;
    let notInCampaign = true;

    checkedIds.forEach(id => {
      const row = rows.find(row => row.id === id);
      if (row) {
        const inThisCampaign = row.campaign_cases.some(
          campaignCase => campaignCase.campaign_id === parseInt(campaignId)
        );

        if (!inThisCampaign) {
          inCampaign = false;
        } else {
          notInCampaign = false;
        }
      }
    });

    return { inCampaign, notInCampaign };
  };

  const removeFromCampaign = async ids => {
    await CaseService.removeCaseFromCampaign({
      case_ids: ids,
      campaign_id: parseInt(campaignId),
    });
    await invalidateData();
  };

  const actionsButton = ({ selectedIds, selectedData }) => {
    const disableActions = selectedIds.length === 0;

    const { inCampaign, notInCampaign } = campaignId
      ? checkSelectedRowsInCampaign(selectedData, selectedIds)
      : { inCampaign: false, notInCampaign: true };

    return (
      <Dropdown
        text="Actions"
        className="button mini"
        disabled={disableActions}
      >
        <Dropdown.Menu direction="left">
          <AssignInvestigatorsModal
            caseIds={selectedIds}
            onSuccess={invalidateData}
          />
          {notInCampaign && (
            <Dropdown.Item
              content="Add To Campaign"
              onClick={() => {
                if (addCampaignCaseModalRef.current) {
                  addCampaignCaseModalRef.current.open(
                    checkCaseEntityValidity(selectedData, selectedIds)
                  );
                }
              }}
            />
          )}
          {campaignId && inCampaign && (
            <Dropdown.Item
              content="Update Campaign Assignee"
              onClick={() => {
                if (updateAssigneeModalRef.current) {
                  updateAssigneeModalRef.current.open(
                    checkCaseEntityValidity(selectedData, selectedIds)
                  );
                }
              }}
            />
          )}
          {campaignId && inCampaign && (
            <ConfirmationModal
              menuTrigger
              actionDescription="Remove From Campaign"
              onConfirm={() => removeFromCampaign(selectedIds)}
              popupPosition="right"
              warning
            >
              <p>Are you sure you want to remove this Case?</p>
            </ConfirmationModal>
          )}
        </Dropdown.Menu>
      </Dropdown>
    );
  };

  return (
    <>
      {campaignId ? (
        <>
          <UpdateCampaignCaseModal
            ref={addCampaignCaseModalRef}
            modalHeader={"Add Case to Campaign"}
            showCaseSelector
            campaignId={campaignId}
            entityId={entityId}
            onSuccess={invalidateData}
          />

          <UpdateCampaignCaseModal
            ref={addSingleCampaignCaseModalRef}
            modalHeader={"Add Case to Campaign"}
            campaignId={campaignId}
            entityId={entityId}
            onSuccess={invalidateData}
          />
          <UpdateCampaignCaseModal
            filterCampaigns
            filterEntities
            ref={updateAssigneeModalRef}
            modalHeader={"Update Campaign Assignee"}
            campaignId={campaignId}
            entityId={entityId}
            onSuccess={invalidateData}
          />
        </>
      ) : (
        <UpdateCampaignCaseModal
          filterEntities
          ref={addCampaignCaseModalRef}
          modalHeader={"Add Case to Campaign"}
          entityId={entityId}
          onSuccess={invalidateData}
        />
      )}

      <BaseTableV3
        columns={columns}
        filters={filters}
        customFields={customFields}
        tableName={CASES_TABLE_CONSTANTS.tableName}
        queryKey={queryKey}
        className={CASES_TABLE_CONSTANTS.className}
        exportTableName={CASES_TABLE_CONSTANTS.exportTableName}
        header={CASES_TABLE_CONSTANTS.header}
        headerIcon={CASES_TABLE_CONSTANTS.headerIcon}
        tagsDropdownParams={{ visible: enableTags }}
        enableAdvancedFilters={filters?.length}
        enableMerge={false}
        enableBatchEdit
        enableSearch
        enableSettings
        invalidateData={invalidateData}
        actionButtons={actionsButton}
        fetchData={fetchCases}
        downloadParams={defaultQueryParams}
        createButton={
          campaignId ? (
            <Button
              size="tiny"
              content="Add Case"
              className="item-adder"
              onClick={() => {
                if (addCampaignCaseModalRef.current) {
                  addCampaignCaseModalRef.current.open();
                }
              }}
            />
          ) : null
        }
      />
    </>
  );
};

export default CasesTableV3;
